import React from 'react';
import { Switch, Text, makeStyles, Tooltip } from '@fluentui/react-components';
import { useEnvironment } from '../../Context/Environment/Context';
import { usePermissions } from '../../Context/Permissions/Context';
import { hasDeveloperPermission } from '../../Context/Permissions/utils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid',
  },
  environmentText: {
    fontWeight: 'bold',
  },
});

interface EnvironmentToggleProps {
  className?: string;
  style?: React.CSSProperties;
}

export const EnvironmentToggle: React.FC<EnvironmentToggleProps> = ({
  className = '',
  style = {},
}) => {
  const { environment, setEnvironment, isDevelopment } = useEnvironment();
  const permissions = usePermissions();
  const styles = useStyles();

  // Only show to users with developer permissions
  if (!hasDeveloperPermission(permissions)) {
    return null;
  }

  const handleToggle = () => {
    setEnvironment(isDevelopment ? 'production' : 'development');
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: isDevelopment ? '#FFF3CD' : '#D1ECF1',
    borderColor: isDevelopment ? '#FFC107' : '#17A2B8',
    color: isDevelopment ? '#856404' : '#0C5460',
    ...style,
  };

  const tooltipContent = `Current environment: ${environment.toUpperCase()}\n${
    isDevelopment ? 'Using localhost endpoints (dev)' : 'Using production endpoints'
  }\nClick to switch environments instantly.`;

  return (
    <Tooltip content={tooltipContent} relationship="label">
      <div className={`${styles.container} ${className}`} style={containerStyle}>
        <Text size={300}>Environment:</Text>
        <Switch
          checked={isDevelopment}
          onChange={handleToggle}
          labelPosition="after"
          label={isDevelopment ? 'DEV' : 'PROD'}
        />
        <Text
          size={300}
          className={styles.environmentText}
          style={{ color: isDevelopment ? '#856404' : '#0C5460' }}
        >
          {environment.toUpperCase()}
        </Text>
      </div>
    </Tooltip>
  );
};
